export const IMAGE_MAX = 8;

const KEY_ATTR = /data-s3-key=["']([^"']+)["']/gi;
const KEY_SRC = /src=["'](posts\/[^"']+)["']/gi;

export const isBlogS3Key = (value) => /^posts\/[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/.test(String(value || ""));

export const extractPendingIds = (html) => {
    const found = [];
    const seen = new Set();
    String(html || "").replace(/data-pending-id=["']([^"']+)["']/gi, (_, id) => {
        if (id && !seen.has(id)) {
            seen.add(id);
            found.push(id);
        }
        return _;
    });
    return found;
};

export const countBodyImages = (html) => extractS3Keys(html).length + extractPendingIds(html).length;

export const rewritePendingToKey = (html, pendingId, s3Key) => String(html || "").replace(
    /<img\b([^>]*)>/gi,
    (tag, attrs) => {
        const id = attrs.match(/data-pending-id=["']([^"']+)["']/)?.[1];
        if (id !== pendingId) return tag;
        let next = attrs
            .replace(/\s*data-pending-id=["'][^"']*["']/, "")
            .replace(/\s*src=["'][^"']*["']/, ` src="${s3Key}"`);
        if (/data-s3-key=/.test(next)) {
            next = next.replace(/data-s3-key=["'][^"']*["']/, `data-s3-key="${s3Key}"`);
        } else {
            next += ` data-s3-key="${s3Key}"`;
        }
        return `<img${next}>`;
    },
);

export const stripPendingPreview = (html) => String(html || "").replace(
    /<img\b([^>]*)>/gi,
    (tag, attrs) => {
        if (!/data-pending-id=/.test(attrs)) return tag;
        return `<img${attrs.replace(/\s*src=["']blob:[^"']*["']/, " src=\"\"")}>`;
    },
);

// Blob previews die on refresh. Drop those tags so a restored draft stays valid.
export const stripUnsavedImages = (html) => String(html || "")
    .replace(/<p>\s*<img\b[^>]*(?:data-pending-id=|src=["']blob:)[^>]*>\s*<\/p>/gi, "")
    .replace(/<img\b[^>]*(?:data-pending-id=|src=["']blob:)[^>]*>/gi, "");

export const extractS3Keys = (html) => {
    const found = [];
    const seen = new Set();
    const add = (key) => {
        if (!isBlogS3Key(key) || seen.has(key)) return;
        seen.add(key);
        found.push(key);
    };
    String(html || "").replace(KEY_ATTR, (_, key) => {
        add(key);
        return _;
    });
    String(html || "").replace(KEY_SRC, (_, key) => {
        add(key);
        return _;
    });
    return found;
};

const imgSrcOf = (attrs) => attrs.match(/src=["']([^"']*)["']/)?.[1] || "";

export const pendingImagesOf = (html) => {
    const found = [];
    String(html || "").replace(/<img\b([^>]*)>/gi, (tag, attrs) => {
        const id = attrs.match(/data-pending-id=["']([^"']+)["']/)?.[1];
        if (id) found.push({ id, tag, src: imgSrcOf(attrs) });
        return tag;
    });
    return found;
};

const restorePendingSrc = (html, pendingId, src) => {
    if (!src) return html;
    return String(html || "").replace(/<img\b([^>]*)>/gi, (tag, attrs) => {
        const id = attrs.match(/data-pending-id=["']([^"']+)["']/)?.[1];
        if (id !== pendingId) return tag;
        if (/\ssrc=/.test(attrs)) {
            return `<img${attrs.replace(/src=["'][^"']*["']/, `src="${src}"`)}>`;
        }
        return `<img src="${src}"${attrs}>`;
    });
};

export const headingsToH3 = (html) => String(html || "")
    .replace(/<h2\b/gi, "<h3")
    .replace(/<\/h2>/gi, "</h3>");

export const spaceBeforeH3 = (html) => headingsToH3(html).replace(
    /(?<!<p><\/p>)(?<!<p>\s*<\/p>)\s*<h3\b/gi,
    "<p></p><h3",
);

export const writerBodyToHtml = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return "";
    const html = /<[a-z][\s\S]*>/i.test(text)
        ? text
        : text.split(/\n{2,}/).map((block) => {
            const line = block.trim();
            if (/^#{2,3}\s+/.test(line)) return `<h3>${line.replace(/^#{2,3}\s+/, "")}</h3>`;
            if (/^[-*]\s+/m.test(line)) {
                const items = line.split(/\n/).filter(Boolean)
                    .map((item) => `<li><p>${item.replace(/^[-*]\s+/, "")}</p></li>`)
                    .join("");
                return `<ul>${items}</ul>`;
            }
            return `<p>${line.replace(/\n/g, "<br>")}</p>`;
        }).join("");
    return spaceBeforeH3(html);
};

// Writer drafts replace the TipTap body. Keep images the author already placed
// (pending blob previews and published S3 keys) so a fill does not wipe them.
export const mergeWriterDraft = (currentHtml, draftHtml) => {
    let next = writerBodyToHtml(draftHtml);
    if (!next) return currentHtml || "";
    const currentPending = pendingImagesOf(currentHtml);
    const draftPending = new Set(extractPendingIds(next));
    const draftKeys = new Set(extractS3Keys(next));

    currentPending.forEach(({ id, src }) => {
        if (draftPending.has(id)) next = restorePendingSrc(next, id, src);
    });

    const extras = [];
    currentPending.forEach(({ id, tag }) => {
        if (!draftPending.has(id)) extras.push(tag);
    });
    extractS3Keys(currentHtml).forEach((key) => {
        if (!draftKeys.has(key)) extras.push(`<img data-s3-key="${key}" src="${key}" alt="">`);
    });
    if (extras.length) {
        next += extras.map((tag) => `<p>${tag}</p>`).join("");
    }
    return spaceBeforeH3(next);
};

export const bodyWithLegacyImages = (html, keys) => {
    const have = new Set(extractS3Keys(html));
    const extras = (keys || []).filter((key) => isBlogS3Key(key) && !have.has(key));
    if (!extras.length) return html || "";
    const blocks = extras
        .map((key) => `<p><img data-s3-key="${key}" src="${key}" alt=""></p>`)
        .join("");
    return `${html || ""}${blocks}`;
};

export const replaceBlogImageSrc = (html, urls) => String(html || "").replace(
    /<img\b([^>]*)>/gi,
    (tag, attrs) => {
        const keyMatch = attrs.match(/data-s3-key=["']([^"']+)["']/)
            || attrs.match(/src=["'](posts\/[^"']+)["']/);
        const key = keyMatch?.[1];
        const url = key ? urls[key] : "";
        if (!url) return tag;
        if (/\ssrc=/.test(attrs)) {
            return `<img${attrs.replace(/src=["'][^"']*["']/, `src="${url}"`)}>`;
        }
        return `<img${attrs} src="${url}">`;
    },
);
